package org.gymly.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.PortalRepo
import org.gymly.model.PortalMe
import org.gymly.theme.GymlyColors
import org.gymly.theme.GymlyFont
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.GymlyLoadingCenter
import org.gymly.ui.components.GymlyPrimaryButton
import org.gymly.ui.components.GymlySecondaryButton
import org.gymly.ui.components.GymlyTextField
import org.gymly.ui.components.PasswordRules
import org.gymly.ui.components.PasswordRulesHint
import org.gymly.util.userMessage

/**
 * Si el socio entró con una contraseña temporal, debe elegir una propia antes de usar el portal o la app.
 * El backend también lo marca; mientras siga marcada, este bloqueo no se levanta.
 */
@Composable
fun PortalPasswordGate(
    onLogout: () -> Unit,
    content: @Composable () -> Unit,
) {
    var me by remember { mutableStateOf<PortalMe?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var attempt by remember { mutableStateOf(0) }

    LaunchedEffect(attempt) {
        loadError = null
        try {
            me = PortalRepo.api.fetchMe()
        } catch (e: Throwable) {
            loadError = e.userMessage("No pudimos cargar tu cuenta. Revisa tu conexión.")
        }
    }

    val current = me
    when {
        current != null && !current.mustChangePassword -> content()
        current != null -> ForcedPasswordChange(
            onChanged = { me = current.copy(mustChangePassword = false) },
            onLogout = onLogout,
        )
        loadError != null -> Column(
            Modifier.fillMaxSize().padding(GymlySpace.lg),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(loadError!!, color = GymlyColors.Error, textAlign = TextAlign.Center)
            Spacer(Modifier.height(GymlySpace.md))
            GymlyPrimaryButton(text = "Reintentar", onClick = { attempt++ })
            Spacer(Modifier.height(GymlySpace.sm))
            TextButton(onClick = onLogout) { Text("Cerrar sesión") }
        }
        else -> GymlyLoadingCenter()
    }
}

/** Pide la contraseña temporal (la que recibió) y una nueva que cumpla la política. */
@Composable
private fun ForcedPasswordChange(
    onChanged: () -> Unit,
    onLogout: () -> Unit,
) {
    var temporary by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var saving by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    fun save() {
        val next = newPassword.trim()
        error = when {
            temporary.isBlank() -> "Escribe la contraseña temporal que recibiste."
            PasswordRules.unmet(next).isNotEmpty() ->
                "La nueva contraseña debe tener al menos 8 caracteres, una letra y un número."
            next == temporary.trim() -> "Elige una contraseña distinta a la temporal."
            next != confirmPassword.trim() -> "Las contraseñas nuevas no coinciden."
            else -> null
        }
        if (error != null) return
        scope.launch {
            saving = true
            try {
                PortalRepo.api.changePassword(temporary.trim(), next)
                onChanged()
            } catch (e: Throwable) {
                error = e.userMessage("No se pudo cambiar la contraseña.")
            } finally {
                saving = false
            }
        }
    }

    Column(
        Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(GymlySpace.lg),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text("Cambia tu contraseña", fontWeight = FontWeight.Bold, fontSize = 20.sp, textAlign = TextAlign.Center)
        Spacer(Modifier.height(GymlySpace.sm))
        Text(
            "Entraste con una contraseña temporal. Elige una propia para seguir usando el portal.",
            color = GymlyColors.TextMuted,
            fontSize = GymlyFont.small,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(GymlySpace.lg))
        Column(Modifier.fillMaxWidth()) {
            GymlyTextField(
                value = temporary,
                onValueChange = { temporary = it },
                placeholder = "Contraseña temporal",
                isPassword = true,
            )
            Spacer(Modifier.height(GymlySpace.sm))
            GymlyTextField(
                value = newPassword,
                onValueChange = { newPassword = it },
                placeholder = "Nueva contraseña",
                isPassword = true,
            )
            PasswordRulesHint(newPassword)
            Spacer(Modifier.height(GymlySpace.sm))
            GymlyTextField(
                value = confirmPassword,
                onValueChange = { confirmPassword = it },
                placeholder = "Repite la nueva contraseña",
                isPassword = true,
            )
        }
        error?.let {
            Spacer(Modifier.height(GymlySpace.sm))
            Text(it, color = GymlyColors.Error, fontSize = GymlyFont.small, textAlign = TextAlign.Center)
        }
        Spacer(Modifier.height(GymlySpace.lg))
        GymlyPrimaryButton(text = "Guardar y entrar", loading = saving, onClick = { save() })
        Spacer(Modifier.height(GymlySpace.sm))
        GymlySecondaryButton(text = "Cerrar sesión", onClick = onLogout)
    }
}
